import React, { useState, useEffect, useRef } from 'react';
import { FaHeart, FaRegHeart, FaMapMarkerAlt, FaBriefcase, FaRulerVertical, FaClock, FaUserCheck, FaLock, FaCrown, FaChevronLeft, FaChevronRight, FaStar, FaShieldAlt, FaBell, FaExclamationTriangle, FaEyeSlash, FaSearch, FaFilter, FaUser, FaGraduationCap, FaPrayingHands, FaVenusMars, FaCheck } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import SkeletonLoader from '../common/SkeletonLoader';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

const Matches = ({ filter = 'all', searchQuery = '', searchFilters = null, onViewProfile, onOpenConversation, onUpgradeClick }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('all');

    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userPlan, setUserPlan] = useState('free');
    const [myProfile, setMyProfile] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [matchesCount, setMatchesCount] = useState(0);
    const [shortlisted, setShortlisted] = useState([]);
    const [showMenuId, setShowMenuId] = useState(null);
    const [planFeatures, setPlanFeatures] = useState({});


    // Report states
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportingProfileId, setReportingProfileId] = useState(null);
    const [reportReason, setReportReason] = useState('');
    const [reporting, setReporting] = useState(false);
    const [reportSuccess, setReportSuccess] = useState(false);

    // Block states
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [blockingProfileId, setBlockingProfileId] = useState(null);
    const [blocking, setBlocking] = useState(false);
    const [blockSuccess, setBlockSuccess] = useState(false);
    const isFetchingRef = useRef(false);
    const shortlistLockRef = useRef(new Set());
    const visitedProfilesRef = useRef(new Set());
    const [visitLimitError, setVisitLimitError] = useState('');



    const ITEMS_PER_PAGE = 12;
    const BASE_URL = api.defaults.baseURL;
    const baseUrl = BASE_URL.replace("/api", "");

    useEffect(() => {
        setPage(1);
    }, [filter, searchQuery, searchFilters, activeTab]);

    useEffect(() => {
        fetchData();
    }, [filter, page, searchFilters, searchQuery, activeTab]);

    const fetchData = async () => {
        if (isFetchingRef.current) return; // ⛔ prevent duplicate call
        isFetchingRef.current = true;

        setLoading(true);
        try {
            await Promise.all([
                fetchMatches(),
                fetchShortlisted(),
                fetchMyProfile()
            ]);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
            isFetchingRef.current = false; // ✅ release lock
        }
    };


    const fetchMatches = async () => {
        try {
            let endpoint = '/matches';
            let params = new URLSearchParams();

            params.append('page', page);
            params.append('limit', ITEMS_PER_PAGE);
            // ✅ TAB FILTER → BACKEND
            if (activeTab) {
                params.append('type', activeTab);
            }


            if (filter === 'search' && searchFilters) {
                endpoint = '/search-profiles';

                // ===== BASIC =====
                if (searchFilters.ageFrom) params.append('ageFrom', searchFilters.ageFrom);
                if (searchFilters.ageTo) params.append('ageTo', searchFilters.ageTo);
                if (searchFilters.heightFrom) params.append('heightFrom', searchFilters.heightFrom);
                if (searchFilters.heightTo) params.append('heightTo', searchFilters.heightTo);
                if (searchFilters.maritalStatus) params.append('maritalStatus', searchFilters.maritalStatus);

                // ===== RELIGION / COMMUNITY =====
                if (searchFilters.religion) params.append('religion', searchFilters.religion);
                if (searchFilters.caste) params.append('caste', searchFilters.caste);
                if (searchFilters.subcaste) params.append('subcaste', searchFilters.subcaste);
                if (searchFilters.gothram) params.append('gothram', searchFilters.gothram);

                // ===== HOROSCOPE =====
                if (searchFilters.star) params.append('star', searchFilters.star);
                if (searchFilters.rasi) params.append('raasi', searchFilters.rasi); 
                if (searchFilters.dosham) params.append('dosham', searchFilters.dosham);

                // ===== EDUCATION & CAREER =====
                if (searchFilters.education) params.append('education', searchFilters.education);
                if (searchFilters.occupation) params.append('occupation', searchFilters.occupation);
                if (searchFilters.employedIn) params.append('employedIn', searchFilters.employedIn);
                if (searchFilters.annualIncome) params.append('income', searchFilters.annualIncome);

                // ===== LOCATION =====
                if (searchFilters.country) params.append('country', searchFilters.country);
                if (searchFilters.state) params.append('state', searchFilters.state);
                if (searchFilters.city) params.append('currentCity', searchFilters.city);
                if (searchFilters.citizenship) params.append('citizenship', searchFilters.citizenship);

                // ===== LIFESTYLE =====
                if (searchFilters.eatingHabits) params.append('diet', searchFilters.eatingHabits);
                if (searchFilters.smokingHabits) params.append('smoking', searchFilters.smokingHabits);
                if (searchFilters.drinkingHabits) params.append('drinking', searchFilters.drinkingHabits);

                // ===== KEYWORDS =====
                if (searchFilters.keywords) params.append('keywords', searchFilters.keywords);
            }

            const response = await api.get(`${endpoint}?${params.toString()}`);

            let results = [];

            if (response.data.matches) {
                results = response.data.matches;
                setTotalPages(response.data.totalPages);
                setMatchesCount(response.data.totalMatches);
                setPlanFeatures(response.data.planFeatures || {});

            } else if (Array.isArray(response.data)) {
                results = response.data;
                setMatchesCount(results.length);
                setTotalPages(Math.ceil(results.length / ITEMS_PER_PAGE));

                const start = (page - 1) * ITEMS_PER_PAGE;
                results = results.slice(start, start + ITEMS_PER_PAGE);
            }

            if (activeTab === 'new') {
                results.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            } else if (activeTab === 'best') {
                results.sort((a, b) => {
                    const scoreA = calculateMatchScore(a, myProfile);
                    const scoreB = calculateMatchScore(b, myProfile);
                    return scoreB - scoreA;
                });
            }

            setMatches(results);
        } catch (error) {
            console.error('Error fetching matches:', error);
            setMatches([]);
        }
    };



    const fetchMyProfile = async () => {
        try {
            const response = await api.get('/dashboard/my-profile');
            setMyProfile(response.data.profile);
            if (response.data.user && response.data.user.subscriptionPlan) {
                setUserPlan(response.data.user.subscriptionPlan);
            }
        } catch (error) {
            console.error('Error fetching my profile:', error);
        }
    };

    const fetchShortlisted = async () => {
        try {
            const response = await api.get('/shortlisted');
            const data = response.data.map(item => ({
                profileId: item._id,
                userId: item.userId?._id
            }));
            setShortlisted(data);
        } catch (error) {
            console.error('Error fetching shortlisted:', error);
        }
    };

    const isProfileShortlisted = (profileId) =>
        shortlisted.some(s => s.profileId === profileId);

    const handleShortlist = async (profile, e) => {
        e.preventDefault();
        e.stopPropagation();

        if (shortlistLockRef.current.has(profile._id)) return; // ⛔ block duplicates
        shortlistLockRef.current.add(profile._id);

        const isShortlisted = isProfileShortlisted(profile._id);

        try {
            await api.post('/shortlist', {
                profileId: profile._id,
                userId: profile.userId
            });

            setShortlisted(prev =>
                isShortlisted
                    ? prev.filter(s => s.profileId !== profile._id)
                    : [...prev, { profileId: profile._id, userId: profile.userId }]
            );
        } catch (error) {
            console.error('Shortlist error:', error);
        } finally {
            setTimeout(() => {
                shortlistLockRef.current.delete(profile._id);
            }, 500); // release lock
        }
    };

    const handleRecordVisit = async (profile) => {
        const key = `visit-${profile._id}`;

        if (visitedProfilesRef.current.has(key)) return;
        visitedProfilesRef.current.add(key);

        try {
            await api.post('/record-visit', {
                visitedProfileId: profile._id,
                userId: profile.userId
            });
        } catch (err) {
            const data = err?.response?.data;

            if (data?.limitExceeded) {
                setVisitLimitError(data.message || 'Daily profile view limit exceeded');

                // auto-hide after 3 seconds
                setTimeout(() => {
                    setVisitLimitError('');
                }, 3000);
            } else {
                console.error(err);
            }
        }
    };



    const handleViewProfile = (profile) => {
        if (onViewProfile) {
            onViewProfile(profile._id); // ✅ only ID
        }
    };


    // Report handlers
    const handleReportProfile = (profileId, e) => {
        e.stopPropagation();
        setShowMenuId(null);
        setReportingProfileId(profileId);
        setReportReason('');
        setReportSuccess(false);
        setShowReportModal(true);
    };

    const submitReport = async () => {
        if (!reportReason.trim()) {
            return;
        }

        setReporting(true);
        try {
            await api.post('/report', { profileId: reportingProfileId, reason: reportReason.trim() });
            setReporting(false);
            setReportSuccess(true);
            setTimeout(() => {
                setShowReportModal(false);
                setReportSuccess(false);
            }, 2000);
        } catch (error) {
            console.error('Report error:', error);
            setReporting(false);
        }
    };

    // Block handlers
    const handleBlockProfile = (profileId, e) => {
        e.stopPropagation();
        setShowMenuId(null);
        setBlockingProfileId(profileId);
        setBlockSuccess(false);
        setShowBlockModal(true);
    };

    const confirmBlock = async () => {
        setBlocking(true);
        try {
            await api.post('/block', { profileId: blockingProfileId });
            setBlocking(false);
            setBlockSuccess(true);

            // Remove from matches list
            setMatches(prev => prev.filter(m => m._id !== blockingProfileId));

            // Auto close after success animation
            setTimeout(() => {
                setShowBlockModal(false);
                setBlockSuccess(false);
            }, 2000);
        } catch (error) {
            console.error('Block error:', error);
            setBlocking(false);
        }
    };

    const calculateMatchScore = (profile, myProfile) => {
        if (!myProfile) return 75;
        let score = 50;
        if (myProfile.religionCommunity?.religion === profile.religionCommunity?.religion) score += 20;
        if (myProfile.religionCommunity?.caste === profile.religionCommunity?.caste) score += 15;
        if (myProfile.location?.currentCity === profile.location?.currentCity) score += 10;
        if (myProfile.educationCareer?.occupation === profile.educationCareer?.occupation) score += 10;
        if (myProfile.basicInfo?.dob && profile.basicInfo?.dob) {
            const calculateExactAge = (dobString) => {
                const dob = new Date(dobString);
                const today = new Date();
                let age = today.getFullYear() - dob.getFullYear();
                const m = today.getMonth() - dob.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
                    age--;
                }
                return age;
            };
            const myAge = calculateExactAge(myProfile.basicInfo.dob);
            const theirAge = calculateExactAge(profile.basicInfo.dob);
            if (Math.abs(myAge - theirAge) <= 5) score += 10;
        }
        return Math.min(score, 99);
    };

    const isNewProfile = (createdAt) => {
        if (!createdAt) return false;
        const profileDate = new Date(createdAt);
        const oneDayAgo = new Date();
        oneDayAgo.setHours(oneDayAgo.getHours() - 24);
        return profileDate > oneDayAgo;
    };

    const getDisplayMatches = () => {
        let filtered = matches;
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(m =>
                m.basicInfo?.firstName?.toLowerCase().includes(query) ||
                m.basicInfo?.lastName?.toLowerCase().includes(query) ||
                m.educationCareer?.occupation?.toLowerCase().includes(query) ||
                m.location?.currentCity?.toLowerCase().includes(query)
            );
        }
        return filtered;
    };

    const displayMatches = getDisplayMatches();

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showMenuId && !event.target.closest('.menu-container')) {
                setShowMenuId(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showMenuId]);

    if (loading) return (
        <div className="w-full mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-4">
            <SkeletonLoader type="card" count={8} />
        </div>
    );

    return (
        <div className="w-full mx-auto px-4 py-8">
            <style>{`
                @media print {
                    body { display: none !important; }
                }
            `}</style>

            {/* Report Modal */}
            <AnimatePresence>
                {showReportModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                        onClick={() => !reportSuccess && setShowReportModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-xl shadow-2xl max-w-md w-full p-8 relative overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {reportSuccess ? (
                                <div className="text-center py-10">
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                                        className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                                    >
                                        <FaCheck className="text-green-600 text-5xl" />
                                    </motion.div>
                                    <h3 className="text-2xl font-bold text-text-primary drop-shadow-sm mb-2">Report Submitted Successfully</h3>
                                    <p className="text-text-secondary">Our team will review it shortly. Thank you for helping keep our community safe.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-red-100 rounded-lg">
                                            <FaExclamationTriangle className="text-red-600 text-xl" />
                                        </div>
                                        <h3 className="text-xl font-bold text-text-primary drop-shadow-sm">Report Profile</h3>
                                    </div>
                                    <p className="text-text-secondary mb-5">
                                        Please let us know why you are reporting this profile. Your report will be reviewed by our team.
                                    </p>
                                    <textarea
                                        value={reportReason}
                                        onChange={(e) => setReportReason(e.target.value)}
                                        placeholder="Describe the issue..."
                                        rows="5"
                                        className="w-full px-4 py-3 border border-glass-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-pink focus:border-transparent resize-none"
                                        disabled={reporting}
                                    />
                                    <div className="flex gap-3 mt-6">
                                        <button
                                            onClick={() => setShowReportModal(false)}
                                            className="flex-1 py-3 border border-glass-border rounded-lg font-medium text-text-secondary hover:bg-[rgba(255,255,255,0.03)]"
                                            disabled={reporting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={submitReport}
                                            disabled={reporting || !reportReason.trim()}
                                            className="flex-1 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {reporting ? 'Submitting...' : 'Submit Report'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Block Modal */}
            <AnimatePresence>
                {showBlockModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                        onClick={() => !blockSuccess && setShowBlockModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-xl shadow-2xl max-w-md w-full p-8 relative overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {blockSuccess ? (
                                <div className="text-center py-10">
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                                        className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                                    >
                                        <FaCheck className="text-green-600 text-5xl" />
                                    </motion.div>
                                    <h3 className="text-2xl font-bold text-text-primary drop-shadow-sm mb-2">Profile Blocked</h3>
                                    <p className="text-text-secondary">You will no longer see this profile.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-[rgba(255,255,255,0.05)] rounded-lg">
                                            <FaEyeSlash className="text-text-secondary text-xl" />
                                        </div>
                                        <h3 className="text-xl font-bold text-text-primary drop-shadow-sm">Block Profile?</h3>
                                    </div>
                                    <p className="text-text-secondary mb-8">
                                        Are you sure you want to block this profile? You won't see them again and they won't be able to contact you.
                                    </p>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setShowBlockModal(false)}
                                            className="flex-1 py-3 border border-glass-border rounded-lg font-medium text-text-secondary hover:bg-[rgba(255,255,255,0.03)]"
                                            disabled={blocking}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={confirmBlock}
                                            disabled={blocking}
                                            className="flex-1 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {blocking ? 'Blocking...' : 'Block Profile'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Header Section */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-text-primary drop-shadow-sm">
                            Find Your Perfect Match
                        </h1>

                        {visitLimitError && (
                            <span className="text-sm text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-md animate-pulse">
                                {visitLimitError}
                            </span>
                        )}
                        <p className="text-text-secondary">
                            Showing <span className="font-semibold text-primary-pink">{displayMatches.length}</span> of {matchesCount} matches
                        </p>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="h-8 w-px bg-[rgba(255,255,255,0.08)]"></div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary-pink">{shortlisted.length}</div>
                            <div className="text-sm text-text-muted">Shortlisted</div>
                        </div>
                    </div>
                </div>

                <div className="flex border-b border-glass-border mb-6">
                    <TabButton
                        active={activeTab === 'all'}
                        onClick={() => setActiveTab('all')}
                        label="All"
                        icon={<FaSearch />}
                    />

                    <TabButton
                        active={activeTab === 'daily'}
                        onClick={() => setActiveTab('daily')}
                        label="Daily Matches"
                        icon={<FaUser />}
                    />

                    <TabButton
                        active={activeTab === 'new'}
                        onClick={() => setActiveTab('new')}
                        label="New Profiles"
                        icon={<FaBell />}
                    />

                    <TabButton
                        active={activeTab === 'best'}
                        onClick={() => setActiveTab('best')}
                        label="Best Matches"
                        icon={<FaHeart />}
                    />

                    <TabButton
                        active={activeTab === 'expectation'}
                        onClick={() => setActiveTab('expectation')}
                        label="Expectation Matches"
                        icon={<FaUserCheck />}
                    />
                </div>

            </div>

            {/* Pagination Top */}
            {totalPages > 1 && (
                <div className="flex justify-between items-center mb-6">
                    <div className="text-sm text-text-secondary">
                        Page {page} of {totalPages}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1}
                            className="p-2 rounded-lg border border-glass-border hover:bg-[rgba(255,255,255,0.03)] disabled:opacity-50"
                        >
                            <FaChevronLeft />
                        </button>
                        <button
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page === totalPages}
                            className="p-2 rounded-lg border border-glass-border hover:bg-[rgba(255,255,255,0.03)] disabled:opacity-50"
                        >
                            <FaChevronRight />
                        </button>
                    </div>
                </div>
            )}

            <AnimatePresence mode="wait">
                {displayMatches.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-lg border border-glass-border p-12 text-center"
                    >
                        <div className="text-5xl mb-4 text-text-muted">💑</div>
                        <h3 className="text-xl font-semibold text-text-primary mb-2">No matches found</h3>
                        <p className="text-text-muted">Try adjusting your search criteria or check back later for new matches.</p>
                    </motion.div>

                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {displayMatches.map((profile, index) => {
                            const isShortlisted = isProfileShortlisted(profile._id);
                            const isNew = isNewProfile(profile.createdAt);
                            const matchScore =
                                typeof profile.matchPercentage === 'number'
                                    ? profile.matchPercentage
                                    : 0;
                            const isVerified = profile.userId?.isVerified === true;
                            const userPlanName = profile.targetPlan
                                ? profile.targetPlan.charAt(0).toUpperCase() + profile.targetPlan.slice(1)
                                : 'free';
                            const calculateExactAge = (dobString) => {
                                if (!dobString) return 'N/A';
                                const dob = new Date(dobString);
                                const today = new Date();
                                let ageVal = today.getFullYear() - dob.getFullYear();
                                const m = today.getMonth() - dob.getMonth();
                                if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
                                    ageVal--;
                                }
                                return ageVal;
                            };
                            const age = calculateExactAge(profile.basicInfo?.dob);
                            const isLocked = profile.lock === true;

                            return (
                                <motion.div
                                    key={profile._id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                    onClick={(e) => {
                                        if (e.target.closest('button')) return;
                                        if (e.target.closest('.menu-container')) return;

                                        if (isLocked) {
                                            if (onUpgradeClick) onUpgradeClick();
                                            return;
                                        }

                                        handleViewProfile(profile);
                                    }}



                                    className={`bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-lg border border-glass-border transition-all duration-300 overflow-hidden group relative
    ${isLocked ? 'cursor-not-allowed opacity-90' : 'hover:border-primary-pink/50 shadow-[0_0_15px_rgba(255,46,136,0.2)] hover:shadow-lg cursor-pointer'}
`}
                                >

                                    <div className={isLocked ? 'pointer-events-none' : ''}>


                                        <div className="relative h-64 bg-[rgba(255,255,255,0.05)] overflow-hidden">
                                            <img
                                                src={
                                                    profile.photos?.[0]
                                                        ? (profile.photos[0].startsWith('http') || profile.photos[0].startsWith('blob:') || profile.photos[0].startsWith('data:')
                                                            ? profile.photos[0]
                                                            : `${baseUrl}${profile.photos[0]}`)
                                                        : (profile.profilePhoto && profile.profilePhoto.length > 100
                                                            ? profile.profilePhoto
                                                            : (profile.basicInfo?.gender === 'Female' ? 'https://randomuser.me/api/portraits/women/85.jpg' : 'https://randomuser.me/api/portraits/men/85.jpg'))
                                                }
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = profile.basicInfo?.gender === 'Female' ? 'https://randomuser.me/api/portraits/women/85.jpg' : 'https://randomuser.me/api/portraits/men/85.jpg';
                                                }}
                                                alt={profile.basicInfo?.firstName}
                                                draggable="false"
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                            {isLocked && (
                                                <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center z-20">
                                                    <FaLock className="text-white text-3xl mb-2" />
                                                    <p className="text-white font-semibold text-sm mb-3">
                                                        Profile Locked
                                                    </p>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (onUpgradeClick) onUpgradeClick();
                                                        }}
                                                        className="px-4 py-2 bg-primary-pink text-white rounded-lg text-sm font-medium hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)]"
                                                    >
                                                        Upgrade to View
                                                    </button>
                                                </div>
                                            )}


                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                            <div className="absolute top-3 left-3 flex flex-col gap-1">
                                                {isNew && (
                                                    <span className="flex items-center gap-1 px-2 py-0.5
                   text-[10px] font-semibold rounded-full
                   bg-[rgba(255,46,136,0.15)] text-primary-pink shadow-sm">
                                                        <FaClock size={9} />
                                                        New
                                                    </span>
                                                )}

                                                <span className="flex items-center gap-1 px-2 py-0.5
                   text-[10px] font-semibold rounded-full
                   bg-yellow-100 text-yellow-800">
                                                    <FaCrown size={10} />
                                                    {userPlanName}
                                                </span>
                                            </div>




                                            <div className="absolute bottom-3 right-3">
                                                <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                                                    <FaStar className="text-yellow-500" size={12} />
                                                    <span className="text-sm font-bold">{matchScore}%</span>
                                                </div>
                                            </div>

                                            <div className="absolute bottom-0 left-0 right-0 p-4">
                                                <h3 className="text-lg font-bold text-white mb-1">
                                                    {profile.basicInfo?.firstName}, {age}
                                                </h3>
                                                <div className="flex items-center text-white/90 text-sm">
                                                    <FaMapMarkerAlt size={12} className="mr-1" />
                                                    <span>{profile.location?.currentCity || 'N/A'}</span>
                                                </div>
                                            </div>

                                            {/* Subtle diagonal center watermark - much more professional */}
                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                <span className="text-white text-2xl font-semibold tracking-widest opacity-30 rotate-[-30deg] select-none">
                                                    Kalyana Matrimony
                                                </span>
                                            </div>
                                        </div>

                                        <div className="p-4">
                                            <div className="grid grid-cols-2 gap-3 mb-4">
                                                <div className="flex items-center gap-2">
                                                    <FaBriefcase className="text-text-muted" size={14} />
                                                    <span className="text-sm text-text-secondary truncate">{profile.educationCareer?.occupation || 'N/A'}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <FaGraduationCap className="text-text-muted" size={14} />
                                                    <span className="text-sm text-text-secondary truncate">{profile.educationCareer?.education || 'N/A'}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <FaPrayingHands className="text-text-muted" size={14} />
                                                    <span className="text-sm text-text-secondary truncate">{profile.religionCommunity?.religion || 'N/A'}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <FaVenusMars className="text-text-muted" size={14} />
                                                    <span className="text-sm text-text-secondary truncate">{profile.basicInfo?.gender || 'N/A'}</span>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={(e) => handleShortlist(profile, e)}
                                                    className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-2 ${isShortlisted
                                                        ? 'bg-[rgba(255,46,136,0.1)] text-primary-pink border border-primary-pink/50 shadow-[0_0_15px_rgba(255,46,136,0.2)]'
                                                        : 'bg-[rgba(255,255,255,0.03)] text-text-secondary hover:bg-[rgba(255,46,136,0.1)] hover:text-primary-pink'
                                                        }`}
                                                >
                                                    {isShortlisted ? <FaHeart /> : <FaRegHeart />}
                                                    <span className="text-sm font-medium">{isShortlisted ? 'Shortlisted' : 'Shortlist'}</span>
                                                </button>

                                                <SendInterestButton
                                                    profile={profile}
                                                    onOpenConversation={onOpenConversation}
                                                    chatAccess={planFeatures.chatAccess}
                                                    onUpgradeClick={onUpgradeClick}
                                                />

                                            </div>
                                        </div>
                                    </div>

                                    <div className="absolute top-2 right-2 flex items-center gap-2 menu-container">
                                        {/* VERIFIED TICK */}
                                        {isVerified && (
                                            <div className="relative group">
                                                <div className="bg-blue-500 text-white p-1.5 rounded-full shadow-md cursor-default flex items-center justify-center" title="Verified Profile">
                                                    <FaUserCheck size={12} />
                                                </div>
                                            </div>
                                        )}

                                        {/* 3 DOT MENU */}
                                        <button
                                            className="text-text-muted hover:text-text-secondary p-2 bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary/80 backdrop-blur-sm rounded-full hover:bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowMenuId(showMenuId === profile._id ? null : profile._id);
                                            }}
                                        >
                                            •••
                                        </button>


                                        {showMenuId === profile._id && (
                                            <div className="absolute right-0 top-10 mt-1 w-40 bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass shadow-lg rounded-lg py-2 border border-glass-border z-20">
                                                <button
                                                    onClick={(e) => handleReportProfile(profile._id, e)}
                                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                >
                                                    <FaExclamationTriangle size={12} /> Report Profile
                                                </button>
                                                <button
                                                    onClick={(e) => handleBlockProfile(profile._id, e)}
                                                    className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-[rgba(255,255,255,0.03)] flex items-center gap-2"
                                                >
                                                    <FaEyeSlash size={12} /> Block Profile
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>

                            );


                        })}

                    </div>


                )}

            </AnimatePresence>


            {/* Pagination Bottom */}
            {totalPages > 1 && (
                <div className="mt-8 flex justify-center">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1}
                            className="px-4 py-2 rounded-lg border border-glass-border hover:bg-[rgba(255,255,255,0.03)] disabled:opacity-50 flex items-center gap-2"
                        >
                            <FaChevronLeft /> Previous
                        </button>

                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (page <= 3) {
                                    pageNum = i + 1;
                                } else if (page >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = page - 2 + i;
                                }

                                return (
                                    <button
                                        key={i}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={`w-8 h-8 rounded-lg ${page === pageNum ? 'bg-primary-pink text-white' : 'border border-glass-border hover:bg-[rgba(255,255,255,0.03)]'}`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page === totalPages}
                            className="px-4 py-2 rounded-lg border border-glass-border hover:bg-[rgba(255,255,255,0.03)] disabled:opacity-50 flex items-center gap-2"
                        >
                            Next <FaChevronRight />
                        </button>
                    </div>
                </div>
            )}

            {/* Safety Notice */}
            <div className="mt-12 bg-[rgba(255,255,255,0.03)] rounded-lg border border-glass-border p-6">
                <div className="flex items-start gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                        <FaShieldAlt className="text-blue-400 text-xl" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-text-primary drop-shadow-sm mb-2">Your Safety is Our Priority</h3>
                        <p className="text-text-secondary text-sm">
                            All profiles are verified through multiple checks. Report any suspicious activity immediately.
                            Your personal information is protected and never shared without your consent.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TabButton = ({ active, onClick, label, icon }) => (
    <button
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-3 font-medium text-sm border-b-2 transition-colors ${active
            ? 'border-rose-600 text-primary-pink'
            : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
    >
        {icon}
        {label}
    </button>
);

const SendInterestButton = ({ profile, onOpenConversation, chatAccess, onUpgradeClick }) => {
    const [sending, setSending] = useState(false);

    const handleSendInterest = async (e) => {
        e.stopPropagation();

        // ❌ No chat access → Upgrade
        if (!chatAccess) {
            if (onUpgradeClick) onUpgradeClick();
            return;
        }

        if (sending) return;

        setSending(true);
        try {
            const targetUserId = profile.userId || profile.user?._id || profile._id;

            const res = await api.post('/chat/interest/send', { targetUserId });
            const conv = res.data.conversation || res.data;

            if (onOpenConversation && conv?._id) {
                onOpenConversation(conv._id);
            }
        } catch (error) {
            console.error('Send interest error:', error);
            alert('Failed to send interest. Please try again.');
        } finally {
            setSending(false);
        }
    };

    return (
        <button
            onClick={handleSendInterest}
            disabled={sending}
            className={`flex-1 py-2 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${chatAccess
                ? 'bg-primary-pink text-white hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)]'
                : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
        >
            {!chatAccess && <FaLock size={12} />}
            <span>
                {chatAccess
                    ? (sending ? 'Connecting...' : 'Connect')
                    : 'Upgrade'}
            </span>
        </button>
    );
};


export default Matches;